import React from 'react';
import { LinearProgress } from '@mui/material';
import { withRouter } from 'react-router-dom';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    LinkCellRenderer,
    TextFilterDefaults,
    booleanCellRenderer,
    BooleanFilterDefaults,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import CommonContext from '../Common';
import { ApiRoutes } from '../ApiRoutes';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import { util } from '../Util';
import CrewForm from './CrewForm';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import './CrewIndex.scss';
import LtsJobCard from '../longTermSchedule/LtsJobCard';

class CrewIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.crewForm = React.createRef();

        this.state = {
            loading: true,
            crews: [],
            showCrewForm: false,
            employees: [],
            equipment: [],
            divisions: [],
        };

        this.onAddCrew = this.onAddCrew.bind(this);
        this.onEditCrew = this.onEditCrew.bind(this);
        this.onCrewFormClosed = this.onCrewFormClosed.bind(this);
        this.yesNoCellRenderer = this.yesNoCellRenderer.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentWillUnmount() {
        return (this.setState = (state, callback) => {});
    }

    onAddCrew = () => {
        this.context.setFormOpened(true);
        this.crewForm.current.openCrew();
    };

    onEditCrew = (crewId) => {
        this.context.setFormOpened(true);
        this.crewForm.current.openCrew(crewId);
    };

    // we use the crewId property on these to determine when
    // we should/not provide an option on the crew form
    refreshEmployeesAndEquipments = async () => {
        const [employees, equipment] = await Promise.all([
            util.fetch.js(ApiRoutes.employees.all()),
            util.fetch.js(ApiRoutes.equipment.all()),
        ]);

        this.setState({ employees, equipment });
    };

    onCrewFormClosed = async () => {
        this.context.setFormOpened(false);

        await this.refreshEmployeesAndEquipments();

        this.state.gridOptions.refresh();
    };

    getEmployees = (params) => {
        if (!params.value) {
            return null;
        }

        const { employees } = this.state;

        const distinctEmployeeIds = params.value || [];

        return employees
            .filter((e) => distinctEmployeeIds.includes(e.id))
            .map((e) => {
                const inactive = e.accountStatusId == 2 ? ' (Inactive)' : '';
                return `${e.lastName}, ${e.firstName} (${e.userName})${inactive}`;
            })
            .join(', ');
    };

    yesNoCellRenderer = booleanCellRenderer;

    getDivision = (params) => {
        if (!params.value) {
            return null;
        }

        const { divisions } = this.state;

        const division = divisions.find((d) => d.id == params.value);

        if (!division) {
            return null;
        }

        return division.name;
    };

    getEquipments = (params) => {
        if (!params.value) {
            return null;
        }

        const { equipment } = this.state;

        const distinctEquipmentIds = params.value || [];

        return equipment
            .filter((eq) => distinctEquipmentIds.includes(eq.id))
            .map((eq) => {
                const inactive = eq.equipmentStatusId == 2 ? ' (Inactive)' : '';

                return `${eq.identifier} - ${eq.description}${inactive}`;
            })
            .join(', ');
    };

    getColor = (params) => {
        if (!params || !params.value) {
            return null;
        }

        return (
            <div
                className="full-size"
                style={{ background: params.value }}
            ></div>
        );
    };

    getOutputPreview = (params) => {
        if (!params || !params.data) {
            return null;
        }

        const { data } = params;

        const { divisions } = this.state;

        const division = divisions.find((d) => d.id == data.divisionId);

        const event = {
            id: data.id,
            text: data.name,
            crewColorHex: data.colorHex,
            divisionColorHex: division.colorHex,
            start: new Date(),
            isPublished: true,
        };

        return <LtsJobCard event={event} />;
    };

    getInactiveComponents = (params) => {
        const { equipment, employees } = this.state;

        if (!params || !params.data) {
            return null;
        }

        const crew = params.data;

        const inactiveEmployees = employees.filter(
            (e) =>
                crew.crewEmployeeMemberships.includes(e.id) &&
                e.accountStatusId == 2
        );

        const inactiveEquipment = equipment.filter(
            (eq) =>
                crew.crewEquipmentMemberships.includes(eq.id) &&
                eq.equipmentStatusId == 2
        );

        return inactiveEmployees.length || inactiveEquipment.length
            ? 'Yes'
            : '';
    };

    getCrewLead = (params) => {
        if (!params || !params.data) {
            return null;
        }

        const { crewLeadEmployeeId } = params.data;

        const { employees } = this.state;

        const employee = employees.find((e) => e.id == crewLeadEmployeeId);

        return employee
            ? `${employee.lastName}, ${employee.firstName} (${employee.userName})`
            : null;
    };

    async populateState() {
        const { crewId } = this.props;

        const [employees, equipment, divisions] = await Promise.all([
            util.fetch.js(ApiRoutes.employees.all()),
            util.fetch.js(ApiRoutes.equipment.all()),
            util.fetch.js(ApiRoutes.division.all()),
        ]);

        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
        };

        const yesNoFilterParams = BooleanFilterDefaults;

        gridOptions.columnDefs = [
            {
                flex: 0,
                width: 80,
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'name',
                headerName: 'Crew Name',
                sortable: true,
                field: 'name',
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: 'nameRenderer',
                cellRendererParams: {
                    clicked: this.onEditCrew,
                    nameField: 'name',
                    idField: 'id',
                    title: 'View this Crew',
                },
            },
            {
                colId: 'divisionName',
                headerName: 'Division',
                sortable: false,
                field: 'divisionId',
                cellRenderer: this.getDivision,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'isInactive',
                sortable: true,
                headerName: 'Crew Inactive',
                field: 'isInactive',
                cellRenderer: this.yesNoCellRenderer,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
            {
                colId: 'crewEmployeeMemberships',
                headerName: 'Employees',
                sortable: false,
                field: 'crewEmployeeMemberships',
                cellRenderer: this.getEmployees,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'crewEquipmentMemberships',
                headerName: 'Equipment',
                sortable: false,
                field: 'crewEquipmentMemberships',
                cellRenderer: this.getEquipments,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'crewLead',
                field: 'crewLead',
                headerName: 'Crew Lead',
                sortable: false,
                cellRenderer: this.getCrewLead,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'inactiveComponents',
                headerName: 'Inactive Pieces?',
                sortable: false,
                cellRenderer: this.getInactiveComponents,
            },
            {
                colId: 'colorHex',
                headerName: 'Calendar Preview',
                sortable: false,
                cellRenderer: this.getOutputPreview,
            },
        ];

        const dataSource = createDataSource(
            ApiRoutes.crew.search(),
            gridOptions
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            equipment,
            employees,
            crewId,
            divisions,
        });

        const { search } = { ...this.props.location };

        if (search) {
            const paramCrewId = new URLSearchParams(search).get('crewId');

            if (paramCrewId) {
                this.onEditCrew(paramCrewId);
            }
        }
    }

    toggleCrewForm = () => {
        const { showCrewForm } = this.state;

        this.setState({ showCrewForm: !showCrewForm });
    };

    render() {
        const {
            rowData,
            gridOptions,
            showCrewForm,
            loading,
            gridApi,
            dataSource,
            gridStatus,
            employees,
            equipment,
            divisions,
        } = this.state;

        if (loading) {
            return <LinearProgress variant="indeterminate" color="secondary" />;
        }

        return (
            <>
                <DataGridToolbar
                    entity="crew"
                    gridApi={gridApi}
                    dataSource={dataSource}
                    gridOptions={gridOptions}
                    onAdd={this.onAddCrew}
                    addLabel="Add Crew"
                    hideExcelButton={true}
                    serverExport={{
                        apiPath: ApiRoutes.crew.excelExport(),
                        filePrefix: 'CrewSearch',
                    }}
                />
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    onNameCellClicked={this.onEditCrew}
                    gridStatus={gridStatus}
                />
                <CrewForm
                    show={showCrewForm}
                    toggleShow={this.toggleCrewForm}
                    ref={this.crewForm}
                    onClose={this.onCrewFormClosed}
                    employees={employees}
                    equipment={equipment}
                    divisions={divisions}
                />
            </>
        );
    }
}

export default withRouter(CrewIndex);
